import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import AvatarStack from '../components/AvatarStack';
import DetailPreviewDescriptionContainer from '../components/DetailPreviewDescriptionContainer';
import SVGIcon from '../components/SVGIcon';
import QuillComponent from '../editor/QuillComponent';
import QuillContext from '../editor/QuillContext';
import InaccessibleMentionNotice from '../editor/mentions/InaccessibleMentionNotice';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { TopicPostsType } from '../graphql/generated/graphql';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import { useAllUsersMap } from '../user/useAllUsers';
import NestableLink from '../utils/NestableLink';
import populateObjects from '../utils/populateObjects';
import { deltaToText } from '../utils/text/deltaText';
import topicPath from '../utils/url/topicPath';
import useIsPublicView from '../utils/useIsPublicView';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import * as styles from './TopicDetailPreview.module.scss';
import TopicNameWithIcon from './TopicNameWithIcon';
import { useAllTopicMap } from './useAllTopics';
import getTopicUsers from './utils/getTopicUsers';

const TITLE_HEIGHT = 55;
const MIN_CONTENT_HEIGHT = 30;
const FOOTER_HEIGHT = 66;

const PUBLIC_TOPIC_DETAIL_PREVIEW_QUERY = gql(/** @complexity 3 */ `
  query PublicTopicDetailPreview($id: ID!) {
    topic: publicTopic(id: $id) {
      id
      ancestors {
        ...TopicBreadcrumbs_topics
      }
      description
      ...TopicNameWithIcon_topic
      ...topicPath_topic
    }
  }
`);

const TOPIC_DETAIL_PREVIEW_QUERY = gql(/** @complexity 8 */ `
  query TopicDetailPreview($id: ID!) {
    topic(id: $id) {
      id
      banner {
        original
      }
      ancestors {
        ...TopicBreadcrumbs_topics
      }
      description
      postCounts {
        count
        type
      }
      descendants(first: 0) {
        totalCount
      }
      ...TopicNameWithIcon_topic
      ...topicPath_topic
    }
  }
`);

interface BaseTopicDetailPreviewProps {
  topic:
    | DocumentType<typeof TOPIC_DETAIL_PREVIEW_QUERY>['topic']
    | DocumentType<typeof PUBLIC_TOPIC_DETAIL_PREVIEW_QUERY>['topic'];
  setMinHeight?: (height: number) => void;
  footer?: React.ReactNode;
}

const BaseTopicDetailPreview = ({
  setMinHeight,
  topic,
  footer,
}: BaseTopicDetailPreviewProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [isContentReady, setIsContentReady] = useState(false);

  const content = topic?.description;

  const hasContent = deltaToText(content);

  const bannerImage = topic && 'banner' in topic && topic.banner?.original;
  const shouldShowFooter = !!footer;

  useEffect(() => {
    if (topic) {
      const minHeight =
        (bannerImage ? parseInt(styles.bannerHeight, 10) : 0) +
        TITLE_HEIGHT +
        (hasContent ? MIN_CONTENT_HEIGHT : 0) +
        (shouldShowFooter ? FOOTER_HEIGHT : 0);

      setMinHeight?.(minHeight);
    }
  }, [bannerImage, hasContent, setMinHeight, topic, shouldShowFooter]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      const verifyContentReady = () => {
        if (contentRef?.current?.getBoundingClientRect().height) {
          setIsContentReady(true);
        }
      };
      const resizeObserver = new ResizeObserver(verifyContentReady);
      resizeObserver.observe(contentRef.current);
      verifyContentReady();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [content]);

  return (
    <div className={styles.container}>
      <NestableLink
        className={styles.overlayLink}
        to={topicPath(topic)}
        ignoreInsideLinks
      >
        <div ref={contentRef} className={styles.content}>
          <div className={styles.headerContainer}>
            {bannerImage && (
              <div
                className={styles.banner}
                style={{
                  backgroundImage: `url(${bannerImage})`,
                }}
              />
            )}
            <div className={styles.header}>
              <TopicBreadcrumbs topics={topic.ancestors} />
              <h3>
                <TopicNameWithIcon iconSize="large" topic={topic} />
              </h3>
            </div>
          </div>
          <DetailPreviewDescriptionContainer
            contentReady={isContentReady}
            content={content}
          >
            {hasContent && (
              <QuillContext>
                <QuillComponent
                  content={content}
                  rootClassName={styles.editorRoot}
                  contentPlaceholder=""
                  namespace="postPreview"
                  readOnly
                  smallTitleSize
                  noTitleLine
                  titlePlaceholder="Untitled"
                />
              </QuillContext>
            )}
          </DetailPreviewDescriptionContainer>
          {!!footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </NestableLink>
    </div>
  );
};

interface TopicDetailPreviewProps {
  topicId: string;
  setMinHeight?: (height: number) => void;
}

const PublicTopicDetailPreview = ({
  topicId,
  setMinHeight,
}: TopicDetailPreviewProps) => {
  const { data: publicTopicData, loading } = useQuery(
    PUBLIC_TOPIC_DETAIL_PREVIEW_QUERY,
    {
      variables: { id: topicId },
    },
  );

  const topic = publicTopicData?.topic;

  const isInaccessible = !loading && !topic;

  useEffect(() => {
    if (isInaccessible) {
      setMinHeight?.(0);
    }
  }, [isInaccessible, setMinHeight]);

  if (isInaccessible) {
    return <InaccessibleMentionNotice mention="topic" />;
  }

  if (topic == null) return null;

  return <BaseTopicDetailPreview topic={topic} setMinHeight={setMinHeight} />;
};

const InternalTopicDetailPreview = ({
  topicId,
  setMinHeight,
}: TopicDetailPreviewProps) => {
  const usersMap = useAllUsersMap();
  const topicMap = useAllTopicMap();

  const { data: topicData, loading } = useQuery(TOPIC_DETAIL_PREVIEW_QUERY, {
    variables: { id: topicId },
  });

  const topic = topicData?.topic;
  const cachedTopic = topicMap[topicId];

  const isInaccessible = !loading && !topic;

  useEffect(() => {
    if (isInaccessible) {
      setMinHeight?.(0);
    }
  }, [isInaccessible, setMinHeight]);

  if (isInaccessible) {
    return <InaccessibleMentionNotice mention="topic" />;
  }

  if (topic == null) return null;

  const { owners, members } = cachedTopic
    ? getTopicUsers(cachedTopic)
    : { owners: [], members: [] };

  const users = populateObjects(usersMap, [...owners, ...members]);

  const publishedPostsCount =
    topic?.postCounts?.find((count) => count.type === TopicPostsType.Published)
      ?.count ?? 0;

  const descendantsCount = topic?.descendants?.totalCount ?? 0;

  const shouldShowFooter =
    publishedPostsCount > 0 || descendantsCount > 0 || users.length > 0;

  return (
    <BaseTopicDetailPreview
      topic={topic}
      setMinHeight={setMinHeight}
      footer={
        shouldShowFooter && (
          <>
            {publishedPostsCount > 0 && (
              <div className={classNames(styles.actionButton, styles.posts)}>
                <SVGIcon className={styles.postsIcon}>{postIcon}</SVGIcon>
                <div className={styles.amount}>{publishedPostsCount}</div>
              </div>
            )}
            {descendantsCount > 0 && (
              <div
                className={classNames(styles.actionButton, styles.subtopics)}
              >
                <SVGIcon className={styles.subtopicsIcon}>
                  {subtopicsIcon}
                </SVGIcon>
                <div className={styles.amount}>{descendantsCount}</div>
              </div>
            )}
            <AvatarStack items={users} limit={5} />
          </>
        )
      }
    />
  );
};

const TopicDetailPreview = (props: TopicDetailPreviewProps) => {
  const isPublicView = useIsPublicView();

  if (isPublicView) {
    return <PublicTopicDetailPreview {...props} />;
  }

  return <InternalTopicDetailPreview {...props} />;
};

export default TopicDetailPreview;
